import React, { Suspense, lazy } from 'react'
import PropTypes from 'prop-types'
import { QUESTION_TYPE_MAP } from 'utils/constants'
import { FroalaTextEditor, Input, Loader } from 'components/UIElements'
import MatrixQuestionsList from './MatrixQuestionsList'

const MatrixChoicesList = lazy(() =>
  import(/* webpackChunkName: "dnd-kit-instrument-matrix" */ './MatrixChoicesList'),
)

const matrixQuestionHasScoring = item => {
  if (item.attributes?.hasScoring) return true
  if (!item.choices_order?.length) return false
  return item.choices_order.some(choiceId => {
    const scoreValue = item.choices?.[choiceId]?.score_value
    return scoreValue !== undefined && scoreValue !== null && scoreValue !== ''
  })
}

class MatrixView extends React.Component {
  componentDidUpdate(prevProps) {
    const { item } = this.props
    if (prevProps.item.type !== item.type) {
      this.el?.querySelector('textarea')?.focus()
    }
  }

  shouldCancelStart = e => {
    const { isActive } = this.props
    return !isActive || !e.target.className.includes('choice-grab-icon')
  }

  onAddChoice = idx => {
    const { item, addChoice, itemId } = this.props
    return () => {
      addChoice(itemId, item.choices_order.length, idx, item.type)
    }
  }

  renderPreviewMatrixTable = () => {
    const { item } = this.props
    const isMultiAnswer = item.attributes?.allowMultipleAnswers
    const hasScoring = matrixQuestionHasScoring(item)
    const choiceIconClass = isMultiAnswer ? 'fa fa-square-o' : 'fa fa-circle-thin'

    return (
      <div className={`matrix-preview-table${hasScoring ? ' has-scoring' : ''}`}>
        <div className='matrix-preview-header'>
          <div className='matrix-preview-question-header' />
          {item.choices_order.map(choiceId => (
            <div key={`matrix-preview-choice-${choiceId}`} className='matrix-preview-choice-header'>
              <FroalaTextEditor value={item.choices[choiceId]?.label || ''} readOnly showToolbar={false} />
              {hasScoring && (
                <Input
                  disabled
                  className='score-val-input'
                  type='number'
                  value={item.choices[choiceId]?.score_value ?? ''}
                />
              )}
            </div>
          ))}
        </div>
        {item.questions_order.map(questionId => (
          <div key={`matrix-preview-row-${questionId}`} className='matrix-preview-row'>
            <div className='matrix-preview-question-cell'>
              <FroalaTextEditor value={item.questions[questionId]?.label || ''} readOnly showToolbar={false} />
            </div>
            {item.choices_order.map(choiceId => (
              <div key={`matrix-preview-cell-${questionId}-${choiceId}`} className='matrix-preview-choice-cell'>
                <i className={choiceIconClass} />
              </div>
            ))}
          </div>
        ))}
      </div>
    )
  }

  render() {
    const { itemId, moveChoice, item, errors, preview } = this.props
    const useXAxis = item.type === QUESTION_TYPE_MAP.likert || item.type === QUESTION_TYPE_MAP.numericRatingScale

    if (preview && item.type === QUESTION_TYPE_MAP.matrix) {
      return <div className='multi-choice-container clinro'>{this.renderPreviewMatrixTable()}</div>
    }

    return (
      <div
        ref={el => {
          this.el = el
        }}
        className='multi-choice-container clinro'>
        <Suspense
          fallback={
            <div className='flexed center-justified' style={{ minHeight: 60 }}>
              <Loader inContainer size={20} />
            </div>
          }>
          <MatrixChoicesList
            onSortEnd={({ oldIndex, newIndex }) => moveChoice(itemId, oldIndex, newIndex)}
            lockAxis={useXAxis ? 'x' : 'y'}
            axis={useXAxis ? 'x' : 'y'}
            lockToContainerEdges
            helperClass='sortableHelper choices'
            shouldCancelStart={this.shouldCancelStart}
            onAddChoice={this.onAddChoice}
            {...this.props}
          />
        </Suspense>
        {errors && !!errors.valuesErr && <p className='survey-error nrs-value matrix'>{errors.valuesErr}</p>}
        {errors && !!errors.blankChoicesErr && (
          <p className='survey-error nrs-value matrix'>{errors.blankChoicesErr}</p>
        )}
        {errors && !!errors.blankScoreErr && <p className='survey-error nrs-value matrix'>{errors.blankScoreErr}</p>}
        <MatrixQuestionsList {...this.props} questionsError={errors?.blankQuestionsErr} />
      </div>
    )
  }
}

MatrixView.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  item: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  errors: PropTypes.object,
  itemId: PropTypes.string,
  moveChoice: PropTypes.func,
  addChoice: PropTypes.func,
  isActive: PropTypes.bool,
  preview: PropTypes.bool,
}

export default MatrixView
